import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as mediaLinkActions from '@redux/modules/mediaLink'
import type { DeleteParams } from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

import { getLinkCapitalizedTitle } from '@functions/mediaLink'

import defaultFormState, { requiredFields } from '@models/mediaLink'
import type { MediaLinkModel, MediaLinkRequestOptions } from '@models/types'

type CreateMediaLinkParams = {
  dispatch: AppDispatch,
  mediaLinkParams: Partial<MediaLinkModel>,
  requestOptions?: MediaLinkRequestOptions,
}

const createMediaLink = (params: CreateMediaLinkParams) => {
  const { dispatch, mediaLinkParams, requestOptions } = params
  const { createMediaLink: createFn } = mediaLinkActions

  return dispatch(createFn(mediaLinkParams, requestOptions))
}

type UpdateMediaLinkParams = {
  dispatch: AppDispatch,
  mediaLink: MediaLinkModel,
  mediaLinkParams: Partial<MediaLinkModel>,
  requestOptions?: MediaLinkRequestOptions,
}

const updateMediaLink = (params: UpdateMediaLinkParams) => {
  const {
    dispatch, mediaLink, mediaLinkParams, requestOptions,
  } = params
  const { updateMediaLink: updateFn } = mediaLinkActions

  const updatedParams = {
    id: mediaLink.id,
    ...mediaLinkParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteMediaLinkParams = {
  dispatch: AppDispatch,
  mediaLink: DeleteParams<MediaLinkModel>,
}

const deleteMediaLink = (params: DeleteMediaLinkParams) => {
  const { dispatch, mediaLink } = params
  const { deleteMediaLink: deleteFn } = mediaLinkActions

  return dispatch(deleteFn(mediaLink))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useMediaLinkForm(
  mediaLink: Partial<MediaLinkModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const mediaLinkForm = useForm(
    defaultFormState,
    { entity: mediaLink, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [mediaLink.id, mediaLink.cache_key],
  )

  return {
    ...mediaLinkForm,
  }
}

export const useRelations = (mediaLink: Partial<MediaLinkModel> = {}) => {
  const {
    linkable_id,
    linkable_type,
  } = mediaLink

  const { campaigns, users } = useSelector(reduxState => reduxState.entities)

  // @ts-expect-error TS2538
  const campaign = linkable_type === 'Campaign' ? campaigns[linkable_id] || {} : {}
  // @ts-expect-error TS2538
  const linkableUser = linkable_type === 'User' ? users[linkable_id] || {} : {}

  return {
    campaign,
    linkableUser,
  }
}

function useMediaLink(initEntity: Partial<MediaLinkModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: mediaLink }: { entity: MediaLinkModel} = useLatestEntity(initEntity, 'mediaLinks')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { campaign, linkableUser } = useRelations(mediaLink)

  const title = getLinkCapitalizedTitle(mediaLink)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.mediaLinks)

  return {
    callbacks: {
      createMediaLink: (mediaLinkParams: Partial<MediaLinkModel>, entityOptions?: MediaLinkRequestOptions) => (
        createMediaLink({ mediaLinkParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditMediaLink: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditMediaLinkModal',
        payload: { mediaLink },
      }),
      deleteMediaLink: () => deleteMediaLink({ dispatch, mediaLink }),
      updateMediaLink: (mediaLinkParams: Partial<MediaLinkModel>, entityOptions?: MediaLinkRequestOptions) => (
        updateMediaLink({
          mediaLink, mediaLinkParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    campaign,
    creating,
    deleting,
    linkableUser,
    loading: loading && !loadedIds.includes(mediaLink.id),
    mediaLink,
    title,
    updating,
  }
}

export default useMediaLink
